import React, { useContext, useState, useEffect } from "react";
import './Cards.css';
import { useNavigate } from "react-router-dom";
import { UserContext } from "../configs/UserContext";
import { deleteProduct, highlightProduct, slugifyText } from "../configs/productsConfig";
import { changeStatus } from "../configs/ordersConfig";
import { domain } from "../configs/auth";

export function ProductCard({ product, tableName, onDelete, page }) {

    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const [isHighlighted, setIsHighlighted] = useState(product.is_highlighted);


    const handleProductClick = () => {
        navigate(`/products/${tableName}/${slugifyText(productData.name)}__${product.product_id}`);
        window.scrollTo(0, 0);
    };

    const handleEditProductClick = () => {
        navigate(`/admin/products/edit/${tableName}/${product.product_id}`);
        window.scrollTo(0, 0);
    };

    const productData = product || '';

    const handleDeleteProduct = async (tableName, productId) => {
        try {
            const result = await deleteProduct(tableName, productId);
            if (result.success) {
                onDelete();
            }
        } catch (error) {
            console.error(error);
        }
    }

    const handleHighlightProduct = async (e) => {
        const newIsHighlighted = e.target.checked;
        try {
            const result = await highlightProduct(tableName, product.product_id, newIsHighlighted);
            if (result.success) {
                setIsHighlighted(newIsHighlighted);
            }
        } catch (error) {
            console.error(error);
        }
    }

    return (
        <div className="card h-100 border-0 shadow-sm rounded-4 bg-light d-flex flex-column overflow-hidden">
    
    <div className="position-relative">
        <img
            src={`${productData.image}`}
            className="img-fluid w-100 object-fit-cover"
            loading="lazy"
            style={{ height: user === 'admin' ? '200px' : '220px', objectFit: 'cover', cursor: 'pointer' }}
            alt="Product"
            onClick={handleProductClick}
        />
    </div>

    <div className="d-flex flex-column p-3 h-100">

        <h5
            className="text-dark fs-6 fw-semibold mb-2 cursor-pointer"
            onClick={handleProductClick}
            style={{ 
                display: '-webkit-box', 
                WebkitBoxOrient: 'vertical', 
                WebkitLineClamp: '2', 
                overflow: 'hidden', 
                textOverflow: 'ellipsis', 
                fontSize: '1rem', 
                minHeight: '2.5rem' // Javítás: maxHeight helyett minHeight, hogy a sorok fixek legyenek
            }}
        >
            {productData.name}
        </h5>

        <div className="d-flex align-items-center mb-2">
            <span className={`me-2 px-1 px-md-2 py-1 rounded-pill ${productData.discount_price === null ? 'bg-white text-dark' : 'bg-danger bg-opacity-10 text-danger'}`}>
                {productData.discount_price === null ? `${productData.price} Ft-tól` : `${productData.discount_price} Ft-tól`}
            </span>

            {productData.discount_price != null && (
                <span className="text-muted text-decoration-line-through">
                    {productData.price} Ft
                </span>
            )}
        </div>

        <div className="d-flex justify-content-center mt-auto">
            {user === 'admin' && page !== 'Home' ? (
                <div className="d-flex gap-2 flex-wrap justify-content-center w-100">
                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteProduct(tableName, productData.product_id)}>Törlés</button>
                    <button className="btn btn-outline-secondary btn-sm" onClick={handleEditProductClick}>Módosítás</button>
                    <input className="form-check-input" type="checkbox" checked={isHighlighted} onChange={(e) => handleHighlightProduct(e)} />
                </div>
            ) : (
                <button
                    className="btn-card rounded-pill px-4 py-2 d-flex align-items-center gap-2"
                    onClick={handleProductClick}
                >
                    Részletek <i className="bi bi-bag-heart-fill"></i>
                </button>
            )}
        </div>

    </div>
</div>

    );
};

export function GuideCard({ product }) {
    return (
        <div className="card mb-4 shadow-sm">

            <img src={`${product.img}`} className="card-img-top img-fluid" alt="Product picture" />

            <div className="card-body d-flex flex-column p-2">
                <h6 className="card-title text-center mb-3" style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: '2',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontSize: '1rem',
                    minHeight: '2.5em'
                }}>
                    {product.name}
                </h6>

                <a className="btn btn-card mx-auto mt-auto" href={domain + product.guide}>
                    Útmutató letöltése
                </a>
            </div>

        </div>
    );
};

export function OrderCard({ order, onStatusChange }) {

    const [isOpen, setIsOpen] = useState(false);
    const [status, setStatus] = useState(order.status);
    const [statusSettings, setStatusSettings] = useState({ text: '', classes: '' });
    const { user } = useContext(UserContext);

    useEffect(() => {
        updateStatusSettings(status);
    }, [status]);

    const updateStatusSettings = (status) => {
        switch (parseInt(status)) {
            case 0:
                setStatusSettings({ text: 'Feldolgozás alatt', classes: 'text-warning' });
                break;
            case 1:
                setStatusSettings({ text: 'Szállítás alatt', classes: 'text-primary' });
                break;
            case 2:
                setStatusSettings({ text: 'Kiszállítva', classes: 'text-success' });
                break;
            case 3:
                setStatusSettings({ text: 'Törölve', classes: 'text-danger' });
                break;
            default:
                setStatusSettings({ text: '', classes: '' });
                break;
        }
    };

    const sendNewStatus = async (e) => {
        const newStatus = e.target.value;
        const orderWithNewStatus = { orderId: order.order_id, status: newStatus };
        try {
            const change = await changeStatus(orderWithNewStatus);
            if (change) {
                setStatus(newStatus);
                if (onStatusChange) onStatusChange();
            } else {
                console.log('Sikertelen frissítés!');
            }
        } catch (err) {
            console.error("Hiba a státusz frissítésekor");
        }
    };

    const orderNumber = order.order_id.split('-')[0];

    return (
        <>
            <div className="d-flex flex-row justify-content-between align-items-center px-2 mb-2">
                <h6 className="m-0 btn" onClick={() => setIsOpen(prevState => !prevState)}>
                    <i className="bi bi-chevron-down"></i> #{orderNumber}
                </h6>

                {user === 'admin' ? (
                    <select
                        className="form-select w-50"
                        value={status}
                        onChange={(e) => sendNewStatus(e)}
                        aria-label="Default select example"
                    >
                        <option value={0}>Feldolgozás alatt</option>
                        <option value={1}>Szállítás alatt</option>
                        <option value={2}>Kiszállítva</option>
                        <option value={3}>Törölve</option>
                    </select>
                ) : (
                    <p className={`m-0 ${statusSettings.classes} fw-semibold`}>{statusSettings.text}</p>
                )}
            </div>

            <table className={`col-12 ${isOpen ? '' : 'd-none'} mb-2`}>
                <tbody>
                    <tr>
                        <td className="ps-2">Termékek</td>
                        <td>Opció</td>
                        <td>Mennyiség</td>
                    </tr>
                    {order.products.map((product, index) => (
                        <tr className="border-top" key={index}>
                            <td className="ps-3">{product.product_name}</td>
                            <td className="ps-3">{product.product_option == 1 ? '' : product.product_option}</td>
                            <td>{product.quantity} db</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <hr />
        </>
    );
};
